import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import {
    AlertCircleIcon,
    ArrowLeftIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    EyeIcon,
    EyeOffIcon,
    HistoryIcon,
    LockIcon,
    PencilIcon,
    PlusIcon,
    RotateCcwIcon,
    Trash2Icon,
} from 'lucide-react';
import QuestionField from '../staffApplication/QuestionField';
import CampAgreementControls from '../CampAgreementControls';
import { sectionIcons } from '../staffApplication/sectionIcons';
import { DEFAULT_STAFF_FORM_SCHEMA, QUESTION_TYPES, isQuestionHideable } from '../../../config/staffFormSchema';
import {
    initStaffFormSchema,
    fetchStaffFormSchema,
    fetchStaffFormDraft,
    listStaffFormVersions,
    addStaffFormQuestion,
    toggleStaffFormQuestion,
    removeStaffFormQuestion,
    moveStaffFormQuestion,
    resetStaffFormSchema,
    publishStaffFormDraft,
    discardStaffFormDraft,
} from '../../../services/staffFormService';
import '../staffApplication/staffApplication.generated.css';

const questionTypes = [
    { value: QUESTION_TYPES.TEXT, label: 'Short text' },
    { value: QUESTION_TYPES.TEXTAREA, label: 'Long text' },
    { value: QUESTION_TYPES.EMAIL, label: 'Email' },
    { value: QUESTION_TYPES.TEL, label: 'Phone' },
    { value: QUESTION_TYPES.NUMBER, label: 'Number' },
    { value: QUESTION_TYPES.DATE, label: 'Date' },
    { value: QUESTION_TYPES.SELECT, label: 'Dropdown' },
    { value: QUESTION_TYPES.CHECKLIST, label: 'Multi-select list' },
    { value: QUESTION_TYPES.YESNO, label: 'Yes / No' },
    { value: QUESTION_TYPES.CHECKBOX, label: 'Agreement checkbox' },
    { value: QUESTION_TYPES.FILE, label: 'File upload' },
    { value: QUESTION_TYPES.SIGNATURE, label: 'Signature' },
    { value: QUESTION_TYPES.INFO, label: 'Info / legal text' },
];
const typeLabel = type => questionTypes.find(item => item.value === type)?.label ?? type;

// Matches the backend's INFO_TONES enum; anything else is rejected by the model.
const infoTones = [
    { value: 'neutral', label: 'Neutral' },
    { value: 'legal', label: 'Legal' },
    { value: 'notice', label: 'Notice' },
];

const EMPTY_DRAFT = { label: '', type: QUESTION_TYPES.TEXT, width: 'half', required: false, options: '', body: '', tone: 'neutral' };

// Matches the reference design's FormBuilder.tsx layout/interactions.
const StaffFormBuilder = props => {
    const history = useHistory();
    const campCode = props.userProfile?.campCode;

    /*
     * `sections` is what the builder DISPLAYS — the unpublished draft when one is open, the live
     * form otherwise. It is not necessarily what an applicant would see; `hasUnpublished` is what
     * says which of the two is on screen.
     *
     * (`draft` below is unrelated — it is the new-question form's own field state, and predates
     * any of this. The unpublished-changes flag is deliberately not called that.)
     */
    const [sections, setSections] = useState(DEFAULT_STAFF_FORM_SCHEMA);
    const [hasUnpublished, setHasUnpublished] = useState(false);
    /*
     * Which draft action is in flight: 'publish', 'discard', or null.
     *
     * A single `publishing` boolean used to cover both, because both need to lock the same
     * controls while a request is out. But the Publish button reads its own label off this, so
     * discarding lit up "Publishing…" on the button beside the one that was actually pressed —
     * announcing the opposite of what was asked for. Naming the action keeps the shared lock
     * while letting each button speak only for itself.
     */
    const [pendingAction, setPendingAction] = useState(null);
    const [activeSectionId, setActiveSectionId] = useState(DEFAULT_STAFF_FORM_SCHEMA[0]?.sectionKey ?? '');
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [error, setError] = useState();

    // Form history (D4). Browse only — a snapshot is immutable and no endpoint writes to one, so
    // selecting a past version can never change the camp's form. The read-only mode below exists
    // to stop a director *believing* they are editing it, not to protect the data.
    const [versions, setVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    // null = the live form, which is the only editable state. A number means history is on screen.
    const [viewingVersion, setViewingVersion] = useState(null);
    const [loadingVersion, setLoadingVersion] = useState(false);

    const readOnly = viewingVersion !== null;

    // The backend also authorizes Admin (10) and Super Director (11), but neither carries a
    // camp association, so they have no campCode to send. Widening this gate needs a camp
    // picker first.
    useEffect(() => {
        if (props.userProfile && props.userProfile.UserType !== 2) {
            history.replace('/personnel');
        }
    }, [props.userProfile, history]);

    // The version list is refreshed rather than patched after a mutation, because every mutation
    // publishes a new version and the endpoints return only `sections` — there is no new version
    // number in the response to append. Failing quietly is deliberate: an unavailable history
    // should cost the dropdown, not the builder.
    const loadVersions = () => listStaffFormVersions(campCode)
        .then(setVersions)
        .catch(() => setVersions([]));

    /*
     * Two calls, in order, and both are needed.
     *
     * init creates the form for a camp that has never opened this page and reconciles one built
     * on an older template — but it is a public route, so it strips the unpublished draft. The
     * draft read is director-gated and returns it, which is what lets a director close the tab
     * mid-edit and come back to their work.
     */
    useEffect(() => {
        if (!campCode) return;
        initStaffFormSchema(campCode)
            .then(({ sections, currentVersion }) => {
                if (sections) setSections(sections);
                setCurrentVersion(currentVersion);
                return fetchStaffFormDraft(campCode);
            })
            .then(({ draftSections }) => {
                if (draftSections) {
                    setSections(draftSections);
                    setHasUnpublished(true);
                }
            })
            .catch(setError);
        loadVersions();
    }, [campCode]);

    /*
     * The browser's own "leave site?" prompt. It is the only guard available here: this app is on
     * react-router v5 with no <Prompt> in use anywhere, and in-app navigation away from the
     * builder does NOT trigger this — the draft simply survives on the server and is picked back
     * up by the effect above. What this catches is the genuinely lossy-feeling case, closing the
     * tab, where the director has no way of knowing the work was kept.
     */
    useEffect(() => {
        if (!hasUnpublished) return undefined;
        const warn = event => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [hasUnpublished]);

    // Every mutation returns the whole updated form. A failed one leaves the current state
    // alone and reports why — without this a statusCode 0 would blank the page.
    //
    // Guarded on readOnly as well as the disabled controls: a stale click landing between
    // selecting a version and the re-render would otherwise mutate the live form using a question
    // key read off a historical one, which is the one way browsing could do damage.
    //
    // Every mutation writes to the DRAFT, so neither currentVersion nor the version list can have
    // moved — publish is the only thing that mints one. Re-reading either here would be two
    // wasted requests per click.
    //
    // `draftSections` is preferred over `sections` because the draft is what the director is
    // editing; `sections` in the same response is the untouched live form. The fallback matters
    // for a boundary move, which changes nothing and returns no draft.
    const applyResult = promise => {
        if (readOnly) return Promise.resolve();
        setError(undefined);
        // Destructured under different names on purpose — `sections` is a state variable in this
        // scope, and shadowing it here reads like a no-op.
        return promise.then(({ sections: published, draftSections }) => {
            const next = draftSections || published;
            if (next) setSections(next);
            setHasUnpublished(Boolean(draftSections));
        }).catch(setError);
    };

    /*
     * Publish. One version for the whole editing session, however many clicks went into it, and
     * the moment the live form changes for applicants.
     *
     * currentVersion comes from the response rather than from `versions[0]`: ensureVersion dedupes
     * on the sections hash, so publishing a shape the camp has held before reuses that older
     * number and currentVersion moves DOWN while a higher one still sits in the list.
     */
    const handlePublish = () => {
        if (readOnly || !hasUnpublished || pendingAction) return Promise.resolve();
        setError(undefined);
        setPendingAction('publish');
        return publishStaffFormDraft(campCode)
            .then(({ sections: published, currentVersion: nextVersion }) => {
                if (published) setSections(published);
                if (nextVersion != null) setCurrentVersion(nextVersion);
                setHasUnpublished(false);
                return loadVersions();
            })
            .catch(setError)
            .finally(() => setPendingAction(null));
    };

    // Discard. The live form and its history are untouched — nothing was ever published from the
    // draft, so there is no version to remove.
    const handleDiscard = () => {
        if (readOnly || !hasUnpublished || pendingAction) return Promise.resolve();
        setError(undefined);
        setPendingAction('discard');
        return discardStaffFormDraft(campCode)
            .then(({ sections: published }) => {
                if (published) setSections(published);
                setHasUnpublished(false);
            })
            .catch(setError)
            .finally(() => setPendingAction(null));
    };

    /*
     * A version that does not exist is NOT an error and NOT a fallback: the endpoint answers
     * statusCode 1 with result null. So `sections` being null is the only signal that the read
     * found nothing, and both loaders below have to check it themselves.
     *
     * Getting this wrong is worse than it sounds. Taking the requested number on trust would
     * leave the banner announcing "Viewing version 999" over whichever form was already on
     * screen — the screen would be lying about which version it is showing, and the builder
     * would be stuck read-only on a version that does not exist.
     */
    const missingVersion = label => new Error(`${label} could not be loaded. It may have been removed.`);

    /*
     * Reloads the editable form after history has replaced it on screen. Read-only, like the
     * plain GET it used to call — but through the builder read, because that is the only one that
     * returns the draft. Browsing history with unpublished changes open must give them back on
     * the way out, not silently drop the director's work.
     */
    const backToCurrent = () => {
        setError(undefined);
        setLoadingVersion(true);
        return fetchStaffFormDraft(campCode)
            .then(({ sections, draftSections, currentVersion }) => {
                if (!sections) throw missingVersion('The current form');
                setSections(draftSections || sections);
                setHasUnpublished(Boolean(draftSections));
                setCurrentVersion(currentVersion);
                setViewingVersion(null);
            })
            .catch(setError)
            .finally(() => setLoadingVersion(false));
    };

    const selectVersion = value => {
        if (value === '') return backToCurrent();
        const wanted = Number(value);
        setError(undefined);
        setLoadingVersion(true);
        return fetchStaffFormSchema(campCode, wanted)
            .then(({ sections, version }) => {
                if (!sections) throw missingVersion(`Version ${wanted}`);
                setSections(sections);
                // The version the server echoed back, not the one requested, so the banner can
                // only ever name what is actually on screen.
                setViewingVersion(version ?? wanted);
            })
            .catch(setError)
            .finally(() => setLoadingVersion(false));
    };

    const activeSection = sections.find(section => section.sectionKey === activeSectionId) ?? sections[0];
    const previewQuestions = activeSection ? activeSection.questions.filter(question => question.enabled) : [];
    const needsOptions = draft.type === QUESTION_TYPES.SELECT || draft.type === QUESTION_TYPES.CHECKLIST;
    // 'info' carries its content in `body` rather than collecting an answer, so it needs its own
    // inputs — without them the question stores body: null and renders as a bare label.
    const isInfo = draft.type === QUESTION_TYPES.INFO;
    const canAdd = Boolean(draft.label.trim()) && (!isInfo || Boolean(draft.body.trim()));

    const handleAdd = e => {
        e.preventDefault();
        if (!canAdd || !activeSection) return;
        applyResult(addStaffFormQuestion(campCode, activeSection.sectionKey, {
            label: draft.label.trim(),
            type: draft.type,
            width: draft.type === QUESTION_TYPES.INFO || draft.type === QUESTION_TYPES.TEXTAREA ? 'full' : draft.width,
            required: draft.required,
            options: needsOptions ? draft.options.split('\n').map(option => option.trim()).filter(Boolean) : undefined,
            body: isInfo ? draft.body.trim() : undefined,
            tone: isInfo ? draft.tone : undefined,
        }));
        setDraft(EMPTY_DRAFT);
    };

    const handleToggle = question => applyResult(toggleStaffFormQuestion(campCode, activeSection.sectionKey, question.questionKey));
    const handleRemove = question => applyResult(removeStaffFormQuestion(campCode, activeSection.sectionKey, question.questionKey));
    const handleMove = (question, direction) => applyResult(moveStaffFormQuestion(campCode, activeSection.sectionKey, question.questionKey, direction));
    const handleReset = () => applyResult(resetStaffFormSchema(campCode));

    if (!activeSection) return null;

    return (
        <main className="staff-application-page tw-w-full tw-bg-sand-100 tw-pb-20">
            <div className="tw-border-b tw-border-sand-200 tw-bg-white">
                <div className="tw-mx-auto tw-flex tw-max-w-7xl tw-flex-wrap tw-items-center tw-gap-4 tw-px-4 tw-py-6 sm:tw-px-6">
                    <div className="tw-min-w-0 tw-flex-1">
                        <Link to="/personnel" className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-brand-700 tw-transition hover:tw-text-brand-900">
                            <ArrowLeftIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                            Back to Application
                        </Link>
                        <h1 className="tw-mt-2 tw-text-2xl tw-font-bold tw-text-gray-900">Form Builder</h1>
                        {/* The old copy promised "Changes apply instantly to the staff
                            application". That is no longer true, and it was the more dangerous
                            half of the old behaviour: edits went live one click at a time, so an
                            applicant could load a form from the middle of an editing session. */}
                        <p className="tw-mt-1 tw-text-sm tw-text-gray-600">
                            {readOnly
                                ? 'Browsing a past version of this form. Nothing here can be changed.'
                                : 'Add, hide, reorder, or remove questions, then publish.'}
                        </p>
                    </div>
                    {/* Two rows, split by what the controls act on rather than by how they look.
                        Above: configuring the questions — agreement text, training guide, reset.
                        Below: the state of the form as a whole — the pending changes, and which
                        version is on screen. Flattened into one row they read as an
                        undifferentiated toolbar, with Publish indistinguishable from Reset. */}
                    <div className="tw-flex tw-flex-col tw-items-end tw-gap-2">
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                            {/* The agreement text and training guide the `readTrainingGuide` and
                                `trainingGuidePdf` questions render from — they belong with the rest
                                of the form's configuration rather than on the personnel list.

                                Hidden while browsing history: they edit the camp's live settings, so
                                leaving them on screen beside a past version would invite a change
                                that has nothing to do with what is being looked at. */}
                            {readOnly ? null : <CampAgreementControls variant="builder" campCode={campCode} />}
                            <button
                                type="button"
                                onClick={handleReset}
                                disabled={readOnly || pendingAction !== null}
                                className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-700 tw-transition hover:tw-bg-sand-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
                                <RotateCcwIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                Reset to default
                            </button>
                        </div>

                        {/* Second row: which version is on screen. The row above configures the
                            questions themselves.

                            Discard and Publish used to sit here, to the left of the dropdown. They
                            were gated on `!readOnly && hasUnpublished` — the exact boolean behind
                            the "unpublished changes" banner below — so the two always appeared and
                            vanished together while sitting ninety lines apart. They now live in
                            that banner, beside the copy that explains what publishing does, which
                            also matches the read-only banner directly above it: icon, explanation,
                            action on the right.

                            That leaves this row meaning one thing, and its condition is a plain
                            `versions.length` rather than a compound that existed only because two
                            unrelated concerns shared the row. */}
                        {versions.length ? (
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                                <label htmlFor="form-version" className="tw-text-xs tw-font-semibold tw-text-gray-500">
                                    Version
                                </label>
                                <div className="tw-relative">
                                    <select
                                        id="form-version"
                                        value={viewingVersion ?? ''}
                                        disabled={loadingVersion}
                                        onChange={e => selectVersion(e.target.value)}
                                        className="tw-appearance-none tw-rounded-lg tw-border tw-border-sand-300 tw-bg-white tw-py-2.5 tw-pl-3.5 tw-pr-9 tw-text-xs tw-font-semibold tw-text-gray-700 focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20 disabled:tw-opacity-60">
                                        <option value="">
                                            {currentVersion == null ? 'Current' : `Current (v${currentVersion})`}
                                        </option>
                                        {/* The current version is in this list too — skipped so it
                                            is not offered twice, once as editable and once as a
                                            read-only snapshot of the same questions. */}
                                        {versions
                                            .filter(item => item.version !== currentVersion)
                                            .map(item => (
                                                <option key={item.version} value={item.version}>
                                                    v{item.version} — {moment(item.createdAt).format('MMM D, YYYY h:mm A')}
                                                </option>
                                            ))}
                                    </select>
                                    <ChevronDownIcon className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {readOnly ? (
                <div className="tw-mx-auto tw-max-w-7xl tw-px-4 tw-pt-6 sm:tw-px-6">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-sand-300 tw-bg-sand-50 tw-p-4">
                        <HistoryIcon className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-gray-500" aria-hidden="true" />
                        <div className="tw-min-w-0 tw-flex-1">
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                                Viewing version {viewingVersion} — read only
                            </p>
                            <p className="tw-mt-0.5 tw-text-xs tw-text-gray-600">
                                This is the form as it stood then. Past versions cannot be edited or restored;
                                staff records already reference them.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={backToCurrent}
                            disabled={loadingVersion}
                            className="tw-rounded-lg tw-bg-brand-700 tw-px-4 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-white tw-transition hover:tw-bg-brand-800 disabled:tw-opacity-60">
                            Back to current
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Not shown while browsing history — the read-only banner above already explains what
                is on screen, and two banners disagreeing about it would be worse than one. The
                draft is still safe; it comes back on "Back to current". */}
            {!readOnly && hasUnpublished ? (
                <div className="tw-mx-auto tw-max-w-7xl tw-px-4 tw-pt-6 sm:tw-px-6">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-brand-200 tw-bg-brand-50 tw-p-4">
                        <PencilIcon className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-brand-600" aria-hidden="true" />
                        <div className="tw-min-w-0 tw-flex-1">
                            <p className="tw-text-sm tw-font-semibold tw-text-brand-900">
                                You have unpublished changes
                            </p>
                            <p className="tw-mt-0.5 tw-text-xs tw-text-brand-800">
                                Applicants still see{currentVersion == null ? ' the published form' : ` v${currentVersion}`}.
                                Publishing applies everything below at once and records it as a single new version.
                            </p>
                        </div>
                        {/* The whole banner is already behind `!readOnly && hasUnpublished`, so
                            these need no condition of their own — which is the point of moving
                            them here. Discard is tinted to the banner rather than carrying the
                            header's neutral sand border, which reads as a stray control once it
                            sits on brand-50. */}
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                            <button
                                type="button"
                                onClick={handleDiscard}
                                disabled={pendingAction !== null}
                                className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-brand-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-brand-900 tw-transition hover:tw-bg-brand-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
                                {pendingAction === 'discard' ? 'Discarding…' : 'Discard changes'}
                            </button>
                            <button
                                type="button"
                                onClick={handlePublish}
                                disabled={pendingAction !== null}
                                className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-brand-600 tw-bg-brand-600 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-white tw-transition hover:tw-bg-brand-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
                                <CheckIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                {pendingAction === 'publish' ? 'Publishing…' : 'Publish changes'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {error ? (
                <div className="tw-mx-auto tw-max-w-7xl tw-px-4 tw-pt-6 sm:tw-px-6">
                    <div role="alert" className="tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-brand-200 tw-bg-brand-50 tw-p-4">
                        <AlertCircleIcon className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-brand-600" aria-hidden="true" />
                        <div className="tw-min-w-0">
                            <p className="tw-text-sm tw-font-semibold tw-text-brand-900">{error.message}</p>
                            {error.fieldErrors?.length ? (
                                <ul className="tw-mt-1.5 tw-list-disc tw-pl-4 tw-text-xs tw-text-brand-800">
                                    {error.fieldErrors.map(fieldError => <li key={fieldError.fieldName}>{fieldError.message}</li>)}
                                </ul>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="tw-mx-auto tw-grid tw-max-w-7xl tw-grid-cols-1 tw-gap-6 tw-px-4 tw-py-8 sm:tw-px-6 lg:tw-grid-cols-[240px_minmax(0,1fr)_360px]">
                <nav aria-label="Sections" className="lg:tw-sticky lg:tw-top-32 lg:tw-self-start">
                    <div className="tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-p-3 tw-shadow-sm">
                        <p className="tw-px-1.5 tw-pb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">Sections</p>
                        <ul className="tw-space-y-0.5">
                            {sections.map(section => {
                                const Icon = sectionIcons[section.icon];
                                const active = section.sectionKey === activeSection.sectionKey;
                                return (
                                    <li key={section.sectionKey}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveSectionId(section.sectionKey)}
                                            aria-current={active ? 'true' : undefined}
                                            className={`tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-lg tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-transition ${active ? 'tw-bg-brand-50 tw-font-semibold tw-text-brand-800' : 'tw-text-gray-600 hover:tw-bg-sand-100'}`}>
                                            {Icon ? <Icon className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-gray-400" aria-hidden="true" /> : null}
                                            <span className="tw-min-w-0 tw-flex-1 tw-truncate">{section.title}</span>
                                            <span className="tw-shrink-0 tw-text-[11px] tw-text-gray-400">{section.questions.filter(q => q.enabled).length}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </nav>

                <div className="tw-space-y-4">
                    <div className="tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-shadow-sm">
                        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-sand-200 tw-px-5 tw-py-4">
                            <div>
                                <h2 className="tw-text-base tw-font-semibold tw-text-gray-900">{activeSection.title}</h2>
                                <p className="tw-mt-0.5 tw-text-xs tw-text-gray-500">{activeSection.questions.length} questions in this section</p>
                            </div>
                        </div>
                        <ul className="tw-divide-y tw-divide-sand-200">
                            {activeSection.questions.length === 0 ? (
                                <li className="tw-px-5 tw-py-8 tw-text-center tw-text-sm tw-text-gray-500">
                                    No questions yet — add one below. Applicants will not see this section until it has a question.
                                </li>
                            ) : null}
                            {activeSection.questions.map((question, index) => (
                                <li key={question.questionKey} className={`tw-flex tw-items-start tw-gap-3 tw-px-5 tw-py-3.5 ${question.enabled ? '' : 'tw-bg-sand-50'}`}>
                                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-pt-0.5">
                                        <button
                                            type="button" aria-label={`Move ${question.label} up`} disabled={readOnly || index === 0}
                                            onClick={() => handleMove(question, -1)}
                                            className="tw-rounded tw-p-0.5 tw-text-gray-400 tw-transition hover:tw-bg-sand-100 hover:tw-text-brand-700 disabled:tw-opacity-30">
                                            <ChevronUpIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button" aria-label={`Move ${question.label} down`} disabled={readOnly || index === activeSection.questions.length - 1}
                                            onClick={() => handleMove(question, 1)}
                                            className="tw-rounded tw-p-0.5 tw-text-gray-400 tw-transition hover:tw-bg-sand-100 hover:tw-text-brand-700 disabled:tw-opacity-30">
                                            <ChevronDownIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                        </button>
                                    </div>
                                    <div className="tw-min-w-0 tw-flex-1">
                                        <p className={`tw-text-sm tw-font-medium ${question.enabled ? 'tw-text-gray-900' : 'tw-text-gray-400'}`}>{question.label}</p>
                                        <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                                            <span className="tw-rounded-full tw-bg-sand-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-gray-500">{typeLabel(question.type)}</span>
                                            {question.required ? <span className="tw-rounded-full tw-bg-brand-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-brand-700">Required</span> : null}
                                            {question.locked ? (
                                                <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-sand-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-gray-500">
                                                    <LockIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                                                    System
                                                </span>
                                            ) : null}
                                            {!question.enabled ? <span className="tw-rounded-full tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-gray-500">Hidden</span> : null}
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                        <button
                                            type="button" onClick={() => handleToggle(question)} disabled={readOnly || !isQuestionHideable(question)}
                                            aria-label={question.enabled ? `Hide ${question.label}` : `Show ${question.label}`}
                                            className="tw-rounded-lg tw-p-2 tw-text-gray-400 tw-transition hover:tw-bg-sand-100 hover:tw-text-brand-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-30">
                                            {question.enabled ? <EyeIcon className="tw-h-4 tw-w-4" aria-hidden="true" /> : <EyeOffIcon className="tw-h-4 tw-w-4" aria-hidden="true" />}
                                        </button>
                                        <button
                                            type="button" onClick={() => handleRemove(question)} disabled={readOnly || question.locked}
                                            aria-label={`Remove ${question.label}`}
                                            className="tw-rounded-lg tw-p-2 tw-text-gray-400 tw-transition hover:tw-bg-brand-50 hover:tw-text-brand-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-30">
                                            <Trash2Icon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Hidden rather than disabled while browsing: a greyed-out "Add a question to
                        Applicant Details" under a past version reads as something that could be
                        re-enabled. There is nothing to add a question to — the snapshot is done. */}
                    {readOnly ? null : (
                    <form onSubmit={handleAdd} className="tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-p-5 tw-shadow-sm">
                        <h2 className="tw-text-base tw-font-semibold tw-text-gray-900">Add a question to {activeSection.title}</h2>
                        <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                            <div className="sm:tw-col-span-2">
                                <label htmlFor="new-label" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Question label</label>
                                <input
                                    id="new-label" type="text" value={draft.label}
                                    onChange={e => setDraft({ ...draft, label: e.target.value })}
                                    placeholder="e.g. Do you hold a current CPR certification?"
                                    className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20" />
                            </div>
                            <div>
                                <label htmlFor="new-type" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Answer type</label>
                                <select
                                    id="new-type" value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}
                                    className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20">
                                    {questionTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="new-width" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Field width</label>
                                <select
                                    id="new-width" value={draft.width} onChange={e => setDraft({ ...draft, width: e.target.value })}
                                    className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20">
                                    <option value="half">Half row</option>
                                    <option value="full">Full row</option>
                                </select>
                            </div>
                            {needsOptions ? (
                                <div className="sm:tw-col-span-2">
                                    <label htmlFor="new-options" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Options — one per line</label>
                                    <textarea
                                        id="new-options" rows={3} value={draft.options}
                                        onChange={e => setDraft({ ...draft, options: e.target.value })}
                                        placeholder={'Yes, current\nExpired\nNever certified'}
                                        className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20" />
                                </div>
                            ) : null}
                            {isInfo ? (
                                <>
                                    <div className="sm:tw-col-span-2">
                                        <label htmlFor="new-body" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Text to display</label>
                                        <textarea
                                            id="new-body" rows={5} value={draft.body}
                                            onChange={e => setDraft({ ...draft, body: e.target.value })}
                                            placeholder={'The waiver, policy or explanation the applicant should read.\n\nLine breaks are preserved.'}
                                            className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20" />
                                        <p className="tw-mt-1.5 tw-text-xs tw-text-gray-500">Info blocks are read-only — they display this text and collect no answer.</p>
                                    </div>
                                    <div>
                                        <label htmlFor="new-tone" className="tw-mb-1.5 tw-block tw-text-sm tw-font-semibold tw-text-gray-800">Style</label>
                                        <select
                                            id="new-tone" value={draft.tone} onChange={e => setDraft({ ...draft, tone: e.target.value })}
                                            className="tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20">
                                            {infoTones.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                                        </select>
                                    </div>
                                </>
                            ) : null}
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 sm:tw-col-span-2">
                                <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-text-sm tw-text-gray-700">
                                    <input
                                        type="checkbox" checked={draft.required}
                                        onChange={e => setDraft({ ...draft, required: e.target.checked })}
                                        className="tw-h-4 tw-w-4 tw-rounded tw-border-sand-300 tw-text-brand-600 focus:tw-ring-brand-500" />
                                    Required to submit
                                </label>
                                <button
                                    type="submit" disabled={!canAdd}
                                    className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-brand-700 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-brand-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
                                    <PlusIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                    Add question
                                </button>
                            </div>
                        </div>
                    </form>
                    )}
                </div>

                <aside aria-label={readOnly ? `Preview of version ${viewingVersion}` : 'Live preview'} className="lg:tw-sticky lg:tw-top-32 lg:tw-self-start">
                    <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-shadow-sm">
                        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-sand-200 tw-px-4 tw-py-3">
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                                {readOnly ? `Version ${viewingVersion} preview` : 'Live preview'}
                            </p>
                            <span className="tw-rounded-full tw-bg-brand-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-brand-700">{previewQuestions.length} visible</span>
                        </div>
                        <div className="tw-max-h-[70vh] tw-space-y-4 tw-overflow-y-auto tw-bg-sand-50 tw-p-4">
                            {previewQuestions.length === 0 ? (
                                <p className="tw-py-8 tw-text-center tw-text-sm tw-text-gray-500">
                                    {activeSection.questions.length === 0
                                        ? 'Nothing to preview yet — this section has no questions.'
                                        : 'All questions in this section are hidden.'}
                                </p>
                            ) : previewQuestions.map(question => (
                                <div key={question.questionKey} className="tw-pointer-events-none">
                                    <QuestionField question={question} value={undefined} onChange={() => undefined} />
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>
        </main>
    );
};

const mapStateToProps = state => ({ userProfile: state.user.userProfile });

export default connect(mapStateToProps)(StaffFormBuilder);
